import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPaperPlane,
  faRobot,
  faSpinner,
  faLayerGroup,
  faCircleInfo,
  faEnvelope,
  faUsers,
  faFileLines,
  faGraduationCap,
  faNewspaper,
  faReply,
  faMagic,
  faBookOpen,
  faCheck,
  faXmark,
  faBook,
  faDownload,
  faShareNodes,
  faHeart,
  faAward,
  faCircleQuestion,
  faBookReader,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import ApiServices from "../Services/ApiServices";
import { storageHelper } from "../../Helpers/storageHelper";
import toast from "react-hot-toast";
import botImage from "../../assets/images/bot.png";
import MiniLoader from "./MiniLoader";
import "./RafiqChatBot.css";

/** Default icons by option.value; callers can set option.icon to override. */
const OPTION_ICONS_BY_VALUE = {
  services: faLayerGroup,
  info: faCircleInfo,
  pricing: faTag,
  contact: faEnvelope,
  about: faUsers,
  exams: faFileLines,
  courses: faGraduationCap,
  blogs: faNewspaper,
  back: faReply,
  rafiq_self_exam: faRobot,
  rafiq_zatouna: faMagic,
  rafiq_zakerly: faBookOpen,
  rafiq_story_learn: faBookReader,
  rafiq: faRobot,
  yes: faCheck,
  no: faXmark,
  generate_from_mozakara: faBook,
  download_certificate: faDownload,
  share_certificate: faShareNodes,
  thanks: faHeart,
  generate_certificate: faAward,
  how_to: faCircleQuestion,
};

const resolveOptionIcon = (option) => {
  if (option == null || typeof option === "string") return null;
  if (option.icon) return option.icon;
  const v = option.value;
  if (v == null || v === "") return null;
  return OPTION_ICONS_BY_VALUE[v] ?? null;
};

/** Chat option chips for student tools under Home/rafiqtool (routes match Header). */
export const getRafiqUserToolChatOptions = () => [
  { text: "اختبار ذكي مع رفيق", value: "rafiq_self_exam" },
  { text: "الزتونة", value: "rafiq_zatouna" },
  { text: "ذاكرلي", value: "rafiq_zakerly" },
  { text: "تعلم بالقصة", value: "rafiq_story_learn" },
];

export const isLoggedInStudentUser = () => {
  const token = storageHelper.getItem("token");
  const userType = storageHelper.getItem("user_type");
  return Boolean(token) && userType === "User";
};

const RafiqChatBot = ({
  isOpen,
  onClose,
  isAIChat = false,
  enableChat = false,
  initialMessage = "",
  options = [],
  onOptionSelect = null,
  onAIMessage = null,
  apiEndpoint = "",
  welcomeMessage = "مرحباً! كيف يمكنني مساعدتك اليوم؟",
  subtitle = "",
  externalLoading = false,
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize chat
  useEffect(() => {
    if (isOpen) {
      setMessages([
        {
          id: Date.now(),
          text: welcomeMessage,
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
      if (Array.isArray(options) && options.length > 0) {
        setCurrentOptions(options);
      } else {
        setCurrentOptions([]);
      }
      setHistory([]);
      setSelectedOption(null);
    }
  }, [isOpen]);

  // Sync options from parent if they arrive slightly after mount or update (e.g. translations)
  useEffect(() => {
    if (isOpen && Array.isArray(options)) {
      if (history.length === 0) {
        setCurrentOptions(options);
      }
    }
  }, [options, isOpen, history.length]);

  // Scroll to bottom when messages or options change
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 150); // Small delay to allow framer-motion to render the DOM elements
    return () => clearTimeout(timer);
  }, [messages, currentOptions]);

  // Handle option selection (Guided Options Mode)
  const handleOptionSelect = async (option) => {
    if (!option) return;

    const optionValue = option.value || option.text || option;
    const optionText = typeof option === "string" ? option : option.text || option.label;

    // Add user message to history
    const userMessage = {
      id: Date.now(),
      text: optionText,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // Mark option as selected
    setSelectedOption(optionValue);

    // Update history
    setHistory((prev) => [...prev, option]);

    // Call callback if provided
    if (onOptionSelect) {
      const result = await onOptionSelect(option, history);
      if (result) {
        // Handle result from callback
        if (result.nextOptions) {
          setCurrentOptions(result.nextOptions);
          setSelectedOption(null); // Reset selection for new options
        }
        if (result.message) {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              text: result.message,
              sender: "bot",
              timestamp: new Date(),
            },
          ]);
        }
        if (result.apiResponse) {
          // Handle API response
          handleAPIResponse(result.apiResponse);
        }
      }
    } else if (option.nextOptions) {
      // If option has nextOptions, update current options
      setCurrentOptions(option.nextOptions);
      setSelectedOption(null); // Reset selection for new options
      if (option.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            text: option.message,
            sender: "bot",
            timestamp: new Date(),
          },
        ]);
      }
    }
  };

  // Handle API response for question generation
  const handleAPIResponse = (apiResponse) => {
    if (Array.isArray(apiResponse) && apiResponse.length > 0) {
      const questionsText = `تم إنشاء ${apiResponse.length} سؤال بنجاح!`;
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: questionsText,
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    }
  };

  // Handle AI chat message send
  const handleSendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setLoading(true);

    try {
      if (onAIMessage) {
        // Use custom handler
        const response = await onAIMessage(inputValue, messages);
        if (response) {
          const responseText = typeof response === 'string' ? response : (response.message || response.text);
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              text: responseText,
              sender: "bot",
              timestamp: new Date(),
            },
          ]);

          if (typeof response === 'object' && response.nextOptions) {
            setCurrentOptions(response.nextOptions);
            setSelectedOption(null);
          } else if (typeof response === 'object' && response.options) {
            setCurrentOptions(response.options);
            setSelectedOption(null);
          }
        }
      } else if (apiEndpoint) {
        // Use API endpoint
        const response = await ApiServices.post(apiEndpoint, {
          message: inputValue,
          history: messages,
        });
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            text: response.message || response.text || "تم إرسال الرسالة",
            sender: "bot",
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      toast.error(error.msg || "حدث خطأ أثناء إرسال الرسالة");
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.",
          sender: "bot",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="rafiq-chatbot-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className="rafiq-chatbot-container"
        initial={{ scale: 0.9, opacity: 0, y: 20, rotateY: -15 }}
        animate={{ scale: 1, opacity: 1, y: 0, rotateY: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          duration: 0.5
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rafiq-chatbot-header">
          <div className="header-left">
            <motion.div
              className="header-icon"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2
              }}
              whileHover={{
                scale: 1.1,
                rotate: [0, -10, 10, -10, 0],
                transition: { duration: 0.5 }
              }}
            >
              <motion.img
                src={botImage}
                alt="رفيق"
                className="bot-icon-image"
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
            <motion.div
              className="header-text"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ margin: 0 }}>رفيق</h3>
                {subtitle && (
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: '#6366f1', 
                    background: '#e0e7ff', 
                    padding: '2px 8px', 
                    borderRadius: '12px',
                    fontWeight: 'bold'
                  }}>
                    {subtitle}
                  </span>
                )}
              </div>
            </motion.div>
          </div>
          <div className="header-actions">
            <motion.button
              className="header-action-btn"
              onClick={onClose}
              title="إغلاق"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </motion.button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="rafiq-chatbot-messages">
          {/* Date Separator */}
          {messages.length > 0 && (
            <motion.div
              className="date-separator"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {new Date().toLocaleDateString("ar-EG", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </motion.div>
          )}

          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                className={`message ${message.sender === "user" ? "user-message" : "bot-message"}`}
                initial={{ opacity: 0, y: 10, x: message.sender === "user" ? -20 : 20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="message-content">
                  <p>{message.text}</p>
                </div>
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString("ar-EG", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Options (Guided Mode or Chat Suggestions) */}
          {currentOptions.length > 0 && (
            <motion.div
              className="options-container"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {currentOptions.map((option, index) => {
                const optionValue = option.value || option.text || option;
                const optionText = typeof option === "string" ? option : option.text || option.label;
                const isSelected = selectedOption === optionValue;
                const optionIcon = resolveOptionIcon(option);

                return (
                  <motion.button
                    key={index}
                    className={`option-button ${isSelected ? "selected" : ""}`}
                    onClick={() => handleOptionSelect(option)}
                    whileHover={!isSelected ? { scale: 1.01 } : {}}
                    whileTap={{ scale: 0.99 }}
                    disabled={loading || externalLoading}
                    type="button"
                  >
                    <span className="option-button-content">
                      {optionIcon ? (
                        <span className="option-icon-wrap" aria-hidden>
                          <FontAwesomeIcon icon={optionIcon} className="option-icon" />
                        </span>
                      ) : null}
                      <span className="option-label">{optionText}</span>
                    </span>
                  </motion.button>
                );
              })}
            </motion.div>
          )}

          {(loading || externalLoading) && (
            <div className="loading-indicator">
              <MiniLoader size={20} color="#7c3aed" variant="dots" />
              <span>جاري المعالجة...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area (AI Mode Only) */}
        {(isAIChat || enableChat) && (
          <div className="rafiq-chatbot-input">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="اكتب رسالتك هنا..."
              disabled={loading}
              className="input-field"
            />
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || loading}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
            </button>
          </div>
        )}

        {/* Footer */}
        <motion.div
          className="rafiq-chatbot-footer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <span className="powered-by">Powered by</span>
          <motion.div
            className="footer-logo"
            whileHover={{ scale: 1.05 }}
          >
            <motion.img
              src={botImage}
              alt="رفيق"
              className="footer-bot-image"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <span className="logo-text">رفيق</span>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default RafiqChatBot;

